import { useTranslation } from "react-i18next";

import { WellevateByFullscript, SvgEmersonLogo } from "@shared/assets/logo";
import FullscriptLogoSvg from "@shared/assets/logo/fullscript-logo-green-leaf-pigeon-text.svg";
import { useSharedGlobalConfig } from "@shared/hooks";
import { l } from "@shared/locales/i18n";
import { Link } from "@shared/react-router-dom";

import { Header } from "../Header/Header";

import * as styles from "./AuthHeader.styles";

interface Props {
  route?: string;
  showFromWellevate?: boolean;
  showFromEmersonEcologics?: boolean;
}

const AuthHeader = ({
  route,
  showFromWellevate = false,
  showFromEmersonEcologics = false,
}: Props) => {
  const { t } = useTranslation();
  const { isFromWellevate, isFromEmersonEcologics, isEmerson } = useSharedGlobalConfig();

  const displayFromWellevate = isFromWellevate && showFromWellevate;
  const displayFromEmersonEcologics = isFromEmersonEcologics && showFromEmersonEcologics;

  const renderWellevateLogo = () => (
    <img
      src={WellevateByFullscript}
      css={[styles.logo.base, styles.logo.wellevate]}
      alt={t(l.companies.Wellevate)}
    />
  );

  const renderEmersonEcologicsLogo = () => (
    <SvgEmersonLogo
      role="img"
      data-testid="emerson-logo"
      data-e2e="emerson-logo"
      aria-label={t(l.companies.EmersonEcologics)}
      css={
        displayFromEmersonEcologics
          ? [styles.logo.base, styles.logo.emersonEcologics]
          : styles.emersonLogo
      }
    />
  );

  const renderCompanyByFullscript = () => {
    if (displayFromWellevate) return renderWellevateLogo();
    if (displayFromEmersonEcologics) return renderEmersonEcologicsLogo();

    return null;
  };

  const renderPlatformLogo = () => {
    if (isEmerson && showFromEmersonEcologics) return renderEmersonEcologicsLogo();

    return (
      <>
        <FullscriptLogoSvg css={styles.svgContainer} data-testid="fullscript-logo" />
        {renderCompanyByFullscript()}
      </>
    );
  };

  const renderLogo = () => {
    const content = (
      <div
        css={[
          styles.logoWrapper.base,
          (displayFromWellevate || displayFromEmersonEcologics || isEmerson) &&
            styles.logoWrapper.fromCompanyByFullscript,
        ]}
      >
        {renderPlatformLogo()}
      </div>
    );

    if (route) {
      return (
        <Link aria-label="login" to={route}>
          {content}
        </Link>
      );
    }

    return content;
  };

  return (
    <Header
      css={(displayFromWellevate || displayFromEmersonEcologics || isEmerson) && styles.header}
    >
      {renderLogo()}
    </Header>
  );
};

export { AuthHeader };
